import React from 'react';
import { X, Download, FileSpreadsheet, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';

interface DownloadModeloModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DownloadModeloModal: React.FC<DownloadModeloModalProps> = ({ isOpen, onClose }) => {

  const generateExampleXlsx = () => {
    const exampleData = [
      // Header
      ['Tipo de Segmentação', 'IDRegra', 'EAN', 'BU', 'Budget', '% Indústria', '% Distribuidor', '% Marketplace', '% Executivo'],

      // Exemplo 1: Segmentação por EAN com múltiplos EANs
      ['EAN', '1093884', 'EAN 1', '', '', 10, 80, 10, 0],
      ['EAN', '1093884', 'EAN 2', '', '', 20, 20, 60, 0],
      ['EAN', '1093884', 'EAN 3', '', '', 0, 0, 50, 50],

      // Linhas vazias para separação visual
      ['', '', '', '', '', '', '', '', ''],
      ['', '', '', '', '', '', '', '', ''],

      // Exemplo 2: Segmentação por BU
      ['BU', '1055234', '', 'PC', 15000, 25, 25, 50, 0],
      ['BU', '1133656', '', 'Foods', 12500, 30, 30, 40, 0],
      ['BU', '1133656', '', 'HC', 12500, 0, 40, 40, 20],

      // Linhas vazias
      ['', '', '', '', '', '', '', '', ''],
      ['', '', '', '', '', '', '', '', ''],

      // Exemplo 3: Segmentação por IDRegra (apenas 1 linha permitida)
      ['IDRegra', '1028465', '', '', 20000, 10, 10, 10, 70],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(exampleData);

    // Definir largura das colunas
    worksheet['!cols'] = [
      { wch: 20 }, // Tipo de Segmentação
      { wch: 12 }, // IDRegra
      { wch: 15 }, // EAN
      { wch: 15 }, // BU
      { wch: 12 }, // Budget
      { wch: 12 }, // % Indústria
      { wch: 14 }, // % Distribuidor
      { wch: 14 }, // % Marketplace
      { wch: 12 }, // % Executivo
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Modelo Importação');

    XLSX.writeFile(workbook, 'Modelo_Importacao_Multipatrocinadores.xlsx');
  };

  const handleDownload = () => {
    generateExampleXlsx();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-600 to-cyan-600 px-6 py-5 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <FileSpreadsheet className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Baixar Modelo XLSX</h2>
                <p className="text-blue-100 text-sm">Modelo para importação de promoções</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="h-6 w-6 text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Alerta principal */}
            <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-amber-900 mb-1">Importante!</h3>
                  <p className="text-sm text-amber-800">
                    Não será possível importar as informações caso algum dos registros apresente erros de validação.
                    O sistema irá acusar todas as inconsistências antes de permitir o envio.
                  </p>
                </div>
              </div>
            </div>

            {/* Instruções */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center space-x-2">
                <Info className="h-5 w-5 text-blue-600" />
                <span>Como usar o modelo</span>
              </h3>

              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    1
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Baixe o arquivo modelo</p>
                    <p className="text-sm text-gray-600">O arquivo contém exemplos práticos de cada tipo de segmentação</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    2
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Preencha seus dados</p>
                    <p className="text-sm text-gray-600">Substitua os exemplos pelos seus dados reais, seguindo o mesmo formato</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    3
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Valide seus dados</p>
                    <p className="text-sm text-gray-600">Faça uma importação teste. O sistema irá apontar todos os erros encontrados</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    4
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Corrija e importe</p>
                    <p className="text-sm text-gray-600">Após corrigir todas as inconsistências, importe definitivamente suas promoções</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Regras de validação */}
            <div className="border-2 border-gray-200 rounded-xl p-5 space-y-4">
              <h3 className="text-lg font-bold text-gray-900">Regras de Validação</h3>

              <div className="space-y-3">
                

                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">EAN vs BU vs IDregra:</span> Cada promoção deve ter uma única segmentação uma vez cadastrada, a segmentação anterior será substituída.
                  </p>
                </div>

                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Porcentagens:</span> A soma das 4 colunas (Indústria + Distribuidor + Marketplace + Executivo) deve ser 100%, ou todas podem estar vazias
                  </p>
                </div>

                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Segmentação por EAN:</span> Apenas uma linha com budget preenchido é necessária para toda a segmentação
                  </p>
                </div>

                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Segmentação por BU:</span> Cada linha pode ter um budget específico
                  </p>
                </div>

                <div className="flex items-start space-x-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Segmentação por IDRegra:</span> Só pode ter uma única linha. Múltiplas linhas resultarão em erro
                  </p>
                </div>
              </div>
            </div>

            {/* Exemplo visual das colunas */}
            <div className="bg-gray-50 rounded-xl p-5 space-y-3">
              <h3 className="text-lg font-bold text-gray-900">Colunas do Modelo</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span className="text-gray-700"><span className="font-semibold">Tipo de Segmentação:</span> IDRegra, BU ou EAN</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span className="text-gray-700"><span className="font-semibold">IDRegra:</span> Identificador único da regra</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span className="text-gray-700"><span className="font-semibold">EAN:</span> Código do produto (se aplicável)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span className="text-gray-700"><span className="font-semibold">BU:</span> Business Unit (se aplicável)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span className="text-gray-700"><span className="font-semibold">Budget:</span> Valor do orçamento</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span className="text-gray-700"><span className="font-semibold">% Indústria:</span> Percentual da indústria</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span className="text-gray-700"><span className="font-semibold">% Distribuidor:</span> Percentual do distribuidor</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span className="text-gray-700"><span className="font-semibold">% Marketplace:</span> Percentual do marketplace</span>
                </div>
                <div className="flex items-center space-x-2 col-span-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span className="text-gray-700"><span className="font-semibold">% Executivo:</span> Percentual do executivo</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              O arquivo contém exemplos de todos os tipos de segmentação
            </p>
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Fechar
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-cyan-800 transition-all shadow-lg shadow-blue-500/25"
              >
                <Download className="h-4 w-4" />
                <span>Baixar Planilha</span>
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
